import { useState, useEffect } from 'react'
import { ChevronDown, Search, Loader2 } from 'lucide-react'
import { useSubjects, useUnits } from '../hooks/usePYQ'
import { YEAR_OPTIONS, SYLLABUS_CODE } from '../types'
import type { PYQFilterState } from '../types'

interface PYQFilterFormProps {
  onSubmit: (filters: PYQFilterState) => void
  isLoading?: boolean
}

export default function PYQFilterForm({ onSubmit, isLoading = false }: PYQFilterFormProps) {
  const currentYear = new Date().getFullYear()

  const [yearStart, setYearStart] = useState(currentYear - 9)
  const [yearEnd, setYearEnd] = useState(currentYear)
  const [selectedSubject, setSelectedSubject] = useState('')
  const [selectedUnit, setSelectedUnit] = useState('')

  // Fetch subjects
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjects(SYLLABUS_CODE)

  // Get selected subject's node code
  const selectedSubjectNode = subjects?.find((s) => s.nodeName === selectedSubject)
  const subjectNodeCode = selectedSubjectNode?.nodeCode || ''

  // Fetch units based on selected subject
  const {
    data: units,
    isLoading: unitsLoading,
    error: unitsError,
  } = useUnits(SYLLABUS_CODE, subjectNodeCode)

  // Reset unit when subject changes
  useEffect(() => {
    setSelectedUnit('')
  }, [selectedSubject])

  // Validate year range
  useEffect(() => {
    if (yearEnd < yearStart) {
      setYearEnd(yearStart)
    }
  }, [yearStart, yearEnd])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedSubject || !selectedUnit) {
      return
    }

    onSubmit({
      yearStart,
      yearEnd,
      subject: selectedSubject as PYQFilterState['subject'],
      unitCode: selectedUnit,
    })
  }

  const isFormValid = selectedSubject && selectedUnit && yearStart <= yearEnd

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Year Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Year Range
        </label>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <select
              value={yearStart}
              onChange={(e) => setYearStart(Number(e.target.value))}
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          <span className="text-gray-500">to</span>
          <div className="relative flex-1">
            <select
              value={yearEnd}
              onChange={(e) => setYearEnd(Number(e.target.value))}
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {YEAR_OPTIONS.filter((year) => year >= yearStart).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subject
        </label>
        <div className="relative">
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            disabled={subjectsLoading}
            className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {subjectsLoading ? 'Loading subjects...' : 'Select a subject'}
            </option>
            {subjects?.map((subject) => (
              <option key={subject.nodeCode} value={subject.nodeName}>
                {subject.nodeName}
              </option>
            ))}
          </select>
          {subjectsLoading ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          )}
        </div>
        {subjectsError && (
          <p className="mt-1 text-sm text-red-500">Failed to load subjects</p>
        )}
      </div>

      {/* Unit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Unit
        </label>
        <div className="relative">
          <select
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
            disabled={!selectedSubject || unitsLoading}
            className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!selectedSubject
                ? 'Select a subject first'
                : unitsLoading
                ? 'Loading units...'
                : 'Select a unit'}
            </option>
            {units?.map((unit) => (
              <option key={unit.nodeCode} value={unit.nodeCode}>
                {unit.nodeName}
              </option>
            ))}
          </select>
          {unitsLoading ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          )}
        </div>
        {unitsError && (
          <p className="mt-1 text-sm text-red-500">Failed to load units</p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!isFormValid || isLoading}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Searching...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Browse Questions
          </>
        )}
      </button>
    </form>
  )
}
